import { makeObservable, computed, runInAction, onBecomeObserved, observable } from "mobx";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { CollectionObservable } from "@/store/collections/CollectionObservable";
import { INoteObservable } from "@/store/note";
import Dexie, { liveQuery, Subscription } from "dexie";
import { IndexedBoolean, Maybe } from "@/domains/common/types";
import { orderBy } from "lodash-es";

const MAX_RECENTS_TO_SHOW = 30;

type SortedRecentNotesIndex = [
  isAvailable: IndexedBoolean,
  lastInteractedAt: string,
  modelId: string,
];

type SortedRecentCollectionsIndex = [
  isAvailable: IndexedBoolean,
  lastInteractedAt: string,
  modelId: string,
];

export class AppStoreRecentItemsStore extends AppSubStore {
  isInitialized = false;

  // By marking this as private, we can control the write access
  private recents: (INoteObservable | CollectionObservable)[] = [];

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);
    makeObservable<this, "recents">(this, {
      isInitialized: true,
      initialize: true,
      initializeLiveQuery: true,
      sortedRecentItemsSubscription: true,
      sortedRecentCollectionsNotInteractedWithByMeIds: true,
      sortedRecentCollectionsInteractedWithByMeIds: true,
      sortedRecentNotesInteractedWithByMeIds: true,
      sortedRecentCollectionsNotInteractedWithByMe: computed,
      sortedRecentCollectionsInteractedWithByMe: computed,
      sortedRecentNotesInteractedWithByMe: computed,
      sortedRecentItemsInteractedWithByMe: computed,
      sortedRecentCollections: computed,
      sortedRecentItems: computed,
      recents: observable,
      prepareItems: false,
    });

    onBecomeObserved(this, "sortedRecentItems", () => this.initialize());
  }

  sortedRecentItemsSubscription: Maybe<Subscription>;

  initialize() {
    if (this.isInitialized) return;
    this.initializeLiveQuery();
    runInAction(() => (this.isInitialized = true));
  }

  initializeLiveQuery() {
    if (this.sortedRecentItemsSubscription) return;
    this.sortedRecentItemsSubscription = liveQuery(async () => {
      const [collectionsNotInteracted, collectionsInteracted, notesInteracted] = await Promise.all([
        // Collections not interacted with me = include only empty last_interacted_at
        this.store.collections.localTable
          .where("[is_available+last_interacted_at+model_id]")
          .between([1, "", Dexie.minKey], [1, "", Dexie.maxKey])
          .reverse()
          .limit(MAX_RECENTS_TO_SHOW)
          .keys(),
        // Collections interacted with by me = include only non-empty last_interacted_at
        this.store.collections.localTable
          .where("[is_available+last_interacted_at+model_id]")
          .aboveOrEqual([1, "\0", Dexie.minKey])
          .reverse()
          .limit(MAX_RECENTS_TO_SHOW)
          .keys(),
        // Notes interacted with by me = include only non-empty last_interacted_at
        this.store.notes.localTable
          .where("[is_available+last_interacted_at+model_id]")
          .aboveOrEqual([1, "\0", Dexie.minKey])
          .reverse()
          .limit(MAX_RECENTS_TO_SHOW)
          .keys(),
      ]);
      return {
        collectionsNotInteracted,
        collectionsInteracted,
        notesInteracted,
      };
    }).subscribe({
      next: ({ collectionsNotInteracted, collectionsInteracted, notesInteracted }) => {
        runInAction(() => {
          this.sortedRecentCollectionsNotInteractedWithByMeIds =
            collectionsNotInteracted as unknown as SortedRecentCollectionsIndex[];
          this.sortedRecentCollectionsInteractedWithByMeIds =
            collectionsInteracted as unknown as SortedRecentCollectionsIndex[];
          this.sortedRecentNotesInteractedWithByMeIds =
            notesInteracted as unknown as SortedRecentNotesIndex[];

          this.prepareItems();
        });
      },
    });
  }

  sortedRecentCollectionsNotInteractedWithByMeIds: SortedRecentCollectionsIndex[] = [];
  get sortedRecentCollectionsNotInteractedWithByMe(): CollectionObservable[] {
    return this.sortedRecentCollectionsNotInteractedWithByMeIds
      .map(([_isAvailable, _lastInteractedAt, modelId]) => this.store.collections.get(modelId))
      .filter(collection => !!collection)
      .filter(collection => collection?.isAvailable);
  }

  sortedRecentCollectionsInteractedWithByMeIds: SortedRecentCollectionsIndex[] = [];
  get sortedRecentCollectionsInteractedWithByMe(): CollectionObservable[] {
    return this.sortedRecentCollectionsInteractedWithByMeIds
      .map(([_isAvailable, _lastInteractedAt, modelId]) => this.store.collections.get(modelId))
      .filter(collection => !!collection)
      .filter(collection => collection?.isAvailable);
  }

  sortedRecentNotesInteractedWithByMeIds: SortedRecentNotesIndex[] = [];
  get sortedRecentNotesInteractedWithByMe(): INoteObservable[] {
    return this.sortedRecentNotesInteractedWithByMeIds
      .map(([_isAvailable, _lastInteractedAt, modelId]) => this.store.notes.get(modelId))
      .filter(note => !!note)
      .filter(note => note?.isAvailable);
  }

  // COMPUTED
  get sortedRecentItemsInteractedWithByMe(): (INoteObservable | CollectionObservable)[] {
    return orderBy(
      [
        ...this.sortedRecentNotesInteractedWithByMe,
        ...this.sortedRecentCollectionsInteractedWithByMe,
      ],
      "lastViewedAt",
      "desc"
    );
  }

  get sortedRecentCollections(): CollectionObservable[] {
    return [
      ...this.sortedRecentCollectionsInteractedWithByMe,
      ...this.sortedRecentCollectionsNotInteractedWithByMe,
    ].slice(0, MAX_RECENTS_TO_SHOW);
  }

  get sortedRecentItems(): (INoteObservable | CollectionObservable)[] {
    return this.recents;
  }

  // The goal here is to complete all sorting and filtering logic
  // BEFORE we hydrate the items
  // and await for hydrations to avoid pesky re-rendering that cause layout shifts
  async prepareItems() {
    const noteIndexes = this.sortedRecentNotesInteractedWithByMeIds;
    const collectionIndexes = this.sortedRecentCollectionsInteractedWithByMeIds;

    // mix them together
    const output: SortedRecentNotesIndex[] = [...noteIndexes, ...collectionIndexes];

    // sort by last_interacted_at and limit to MAX_RECENTS_TO_SHOW
    output
      .sort(
        ([_, aLastInteractedAt], [__, bLastInteractedAt]) =>
          new Date(bLastInteractedAt).getTime() - new Date(aLastInteractedAt).getTime()
      )
      .slice(0, MAX_RECENTS_TO_SHOW);

    // hydrate the output
    const items = await Promise.all(
      output.map(async ([_, __, modelId]) => {
        const isCollection = collectionIndexes.some(([, , id]) => id === modelId);

        return isCollection
          ? await this.store.collections.getAsync(modelId)
          : await this.store.notes.getAsync(modelId);
      })
    );

    runInAction(() => {
      // this is a legit filter now because we awaited for hydrations
      this.recents = items.filter(item => !!item);
    });
  }
}
